import { useState } from 'react';
import { FormControl, Stack, FormLabel, Box, Text } from '@chakra-ui/react';
import { Field } from 'formik';
import TagInput from 'components/v4/ChipInput/TagInput';
import TriggerFieldToolTip from './TriggerFieldToolTip';
import { useTriggerForm } from '../useTriggerForm.js';
import { FORM_FIELD_PATHS } from '../triggerFormConstants.js';
import {
    stackSpacing,
    formLabelStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import walkthroughIds from '../../walkthroughIds';

const isValidToken = (v) =>
    v === '[EmailAddress]' || (v.length > 0 && v.includes('@'));

const ChipInputField = ({ field, form, setCursorPosition }) => {
    const { formData, updateFormData, emailAddressTmp, setEmailAddressTmp } =
        useTriggerForm();

    const updateEmailAddresses = (newEmails) => {
        updateFormData(FORM_FIELD_PATHS.EMAIL_ADDRESS, newEmails);
        form.setFieldValue(field.name, newEmails);
    };

    const emailAddressData = formData?.action?.emailAddress;
    const currentEmails = Array.isArray(emailAddressData)
        ? emailAddressData
        : typeof emailAddressData === 'string' && emailAddressData.trim() !== ''
          ? emailAddressData.split(',').map((email) => email.trim())
          : [];

    const commitInput = (raw) => {
        const newEmails = raw
            ?.split(',')
            .map((v) => v.trim())
            .filter(isValidToken);

        if (newEmails?.length > 0) {
            updateEmailAddresses([...currentEmails, ...newEmails]);
        }
        setEmailAddressTmp('');
    };

    return (
        <TagInput
            id="emailAddressField"
            chips={currentEmails}
            onRemoveChip={(_, index) => {
                updateEmailAddresses(
                    currentEmails.filter((_, i) => i !== index),
                );
            }}
            inputValue={emailAddressTmp}
            onInputChange={(e) => setEmailAddressTmp(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    commitInput(emailAddressTmp);
                }
            }}
            onSelect={(e) => setCursorPosition?.(e.target.selectionStart || 0)}
            onFocus={() => setCursorPosition?.(0)}
            onBlur={() => {
                commitInput(emailAddressTmp);
                form.setFieldTouched(field.name, true);
            }}
            placeholder="Enter email addresses..."
            data-walkthroughid={walkthroughIds.editMode.ufEmailAddressInput}
        />
    );
};

const EmailAddressInput = ({
    darkMode,
    setCursorPosition,
    errors,
    touched,
}) => {
    const { editMode } = walkthroughIds;
    const [isHovered, setIsHovered] = useState(false);

    return (
        <FormControl
            sx={{ margin: '0 !important', position: 'relative' }}
            isInvalid={!!errors?.emailAddress && touched?.emailAddress}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                height="auto"
                sx={{ maxWidth: '775px', height: 'auto' }}
                align="center">
                <Box>
                    <FormLabel
                        htmlFor="emailAddressField"
                        sx={formLabelStyling(darkMode)}>
                        Email Addresses
                    </FormLabel>
                    <Text sx={descriptionStyling} maxWidth={250}>
                        Enter email addresses that will receive notifications
                        when this trigger is activated.
                    </Text>
                </Box>
                <Box flex={1} maxWidth="400px">
                    <Field
                        component={ChipInputField}
                        id="emailAddress"
                        name="emailAddress"
                        setCursorPosition={setCursorPosition}
                        data-walkthroughid={editMode.ufEmailAddressInput}
                        validate={(value) => {
                            if (Array.isArray(value)) {
                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                const invalidEmails = value.filter((email) => {
                                    if (email === '[EmailAddress]') {
                                        return false;
                                    }

                                    return !emailRegex.test(email);
                                });
                                if (invalidEmails.length > 0) {
                                    return `Invalid email format: ${invalidEmails.join(
                                        ', ',
                                    )}`;
                                }
                            }
                        }}
                    />
                </Box>
                <TriggerFieldToolTip
                    isVisible={isHovered}
                    label="Enter email addresses separated by commas, spaces, or press Enter to add. Each email should be no more than 100 characters."
                    ariaLabel="email-address-tip"
                />
            </Stack>
        </FormControl>
    );
};

export default EmailAddressInput;
